import { Delete } from '@mui/icons-material';
import {
    Box,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableRow,
    Toolbar,
    Typography
} from '@mui/material';
import React from 'react';
import { TableData, useOCRStore } from '../store/ocrStore';
import { DataTableCell } from './DataTableCell';

interface DataTableProps {
    tableId: string;
    onDelete: (annotationId: string) => void;
    cellRenderers: Record<string, React.FC<any>>;
    usedSensorIds: Set<string>;
}

const DataTableComponentInternal: React.FC<DataTableProps> = ({
    tableId,
    onDelete,
    cellRenderers,
    usedSensorIds
}) => {
    const tableData = useOCRStore(state => state.tables[tableId]) as TableData;

    if (!tableData) {
        return null;
    }

    const { title, grid, annotationId } = tableData;
    
    return (
        <Paper
            elevation={0}
            id={`data-table-${tableData.id}`}
            sx={{
                mb: 3,
                borderRadius: 2,
                overflow: 'hidden',
                border: '1px solid',
                borderColor: 'divider'
            }}
        >
            <Toolbar
                sx={{
                    backgroundColor: 'grey.50',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    minHeight: '44px !important'
                }}
            >
                <Typography
                    variant="h3"
                    sx={{
                        flex: 1,
                        fontWeight: 600,
                        color: 'text.primary'
                    }}
                >
                    {title}
                </Typography>
                <IconButton
                    onClick={() => onDelete(annotationId)}
                    color="error"
                    sx={{
                        '&:hover': {
                            backgroundColor: 'error.light',
                            color: 'error.contrastText'
                        }
                    }}
                >
                    <Delete />
                </IconButton>
            </Toolbar>

            <Box sx={{ maxHeight: 600, overflow: 'auto' }}>
                <Table
                    size="small"
                    stickyHeader
                    sx={{
                        '& .MuiTableCell-root': {
                            borderRight: '1px solid',
                            borderColor: 'divider',
                            '&:last-child': {
                                borderRight: 'none'
                            }
                        }
                    }}
                >
                    <TableBody>
                        {grid.map((row, rIdx) => (
                            <TableRow
                                key={rIdx}
                                sx={{
                                    '&:nth-of-type(even)': {
                                        backgroundColor: 'grey.50'
                                    },
                                    '&:hover': {
                                        backgroundColor: 'action.hover',
                                        '& .MuiTableCell-root': {
                                            backgroundColor: 'transparent'
                                        }
                                    }
                                }}
                            >
                                {row.map((cell, cIdx) => (
                                    <DataTableCell
                                        key={cell?.id ?? `${rIdx}-${cIdx}`}
                                        tableId={tableId}
                                        rowIndex={rIdx}
                                        colIndex={cIdx}
                                        cellRenderers={cellRenderers}
                                        usedSensorIds={usedSensorIds}
                                        annotationId={annotationId}
                                    />
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Box>
        </Paper>
    );
};

export const DataTableComponent = React.memo(DataTableComponentInternal);